// Footmen Wars match sim (2026-10-25). Grown from the 600-unit spike (tinwhistlegames-ops docs/games/footies/spike,
// RESULTS.md): shared flow fields, a spatial hash, structure-of-arrays storage, a fixed 20 Hz step and a seeded Rng, so
// the browser, a future server and the headless balance harness all run the same deterministic match. No DOM, no three.
//
// The match (PLAN.md): four corner barracks spawn units from their slots; units march by their owner's goal (the center,
// a base, or a tapped point) and fight anything of another team on the way. Gold only from kills. Tier up at the gates
// to unlock classes and slots; the first tier-up picks the army. Bases can't be hurt for the first 5 minutes; from
// minute 18 every base decays and the center sends waves at the leader, so a match closes. Last base standing wins.
// Heroes (2026-10-26): one a player, picked at 0:00. A hero is a unit in the same arrays (class 'hero', heroOf marks
// whose), so targeting, the hash and separation treat it like any soldier; its level, mana, skills and revive timer
// live on the player (HeroState). It walks with its army, levels from kills near it, and gets mana fast and the shop at
// the center.
// The center's boss (2026-10-26): from 5:00 the Drum Giant wakes on the center tower every 4:00, with a camp of
// neutrals on each living team's road in (defs.ts BOSS). Neutrals that hold a spot carry post[i] (a camp's team, or
// PLAYERS for the giant) and only fight what comes near it; the giant is one unit (this.boss) with a big body (rad[i]),
// which every reach and area check measures to its edge. Its kill pays the slayer's team gold and the Giant's Drum.
// Unit control (2026-10-26, branch controls; docs/CONTROLS.md): the army goal stays every soldier's default. An 'order'
// sends chosen units (named by uid, so a slot reused between a click and a networked tick never takes someone else's
// order) to a goal of their own, attack-moving or (move) walking past fights until they arrive; a goal of null sends
// them back to the army. Units can belong to one of GROUPS numbered groups (squad[i]); a group remembers its members'
// shared order so the recruits a 'rally' sends to it walk there too. A 'goal' calls every soldier outside a group back.
import { atan2, hypot, sin, sq } from './dmath';
import { Rng } from './rng';
import { retreatTap, arrivalRadius } from './order-rules';
import {
  AGGRO, AURA, BASE, C, CENTER, TOWER, CLASS, CLASSES, ClassDef, ClassId, DT, ECON, FACTION, FACTIONS, FactionDef, FactionId, SLOTS, THINK,
  TIER_COST, TIER_GATE, armorTaken, classBonus, classOf, unitStats, HERO, HEROES, HERO_RULES as HR, HeroId, ITEM, ITEMS, ItemId, Kind, heroStats,
  BOSS, bossAt, BUDGET_HP, BUDGET_DPS, EXTRA, SALVE,
} from './defs';

export const SIZE = 144;
export const PLAYERS = 4;
export const NEUTRAL = 4; // the center's waves, its giant and the camps
/** Room for neutrals: the hard end's waves (48), the giant and a camp for each team. */
const WAVE_CAP = 48; // the hard end's marching neutrals
const NEUTRAL_CAP = WAVE_CAP + 1 + PLAYERS * BOSS.camp.length; // and room kept for the giant and every camp
export const CAP = PLAYERS * (ECON.cap + 1) + NEUTRAL_CAP; // every army, its hero and the neutrals
export const STATE = { dead: 0, walk: 1, attack: 2, idle: 3 } as const;
/** Numbered groups a player can keep (docs/CONTROLS.md: four fit a phone's thumb rail; Red Alert's iPhone port had 4). */
export const GROUPS = 4;
/** Live orders a player may have at once (each point order holds a flow field, about 250 KB). */
export const ORDER_CAP = 12;
/** Playtest: the tower plinth has a radius of 4.6 m; leave room for bodies around it. */
export const PLAZA_RADIUS = 19.5, TOWER_RADIUS = 5, PLINTH_RADIUS = 4.6;
const HASH = 2;
const HW = Math.ceil(SIZE / HASH);

export type FlowField = { dx: Float32Array; dy: Float32Array; dist: Float32Array };
export type Goal = { kind: 'center' } | { kind: 'base'; p: number } | { kind: 'point'; x: number; y: number };
export type Command =
  | { t: 'tier'; faction?: FactionId }
  | { t: 'slot'; i: number; cls: ClassId }
  /** back: fall back to your own base past the fight (a player's Defend); a bot's defend goal leaves it off */
  | { t: 'goal'; goal: Goal; back?: boolean }
  /** Units (by uid) to a goal of their own; move: walk past fights until there (a retreat); goal null: back to the army. */
  | { t: 'order'; ids: number[]; goal: Goal | null; move?: boolean; tap?: { x: number; y: number } }
  /** Make units (by uid) group k (0 to GROUPS - 1), or with add, add them to it. */
  | { t: 'group'; k: number; ids: number[]; add?: boolean }
  /** Where new soldiers go: group k, or -1 the army. */
  | { t: 'rally'; k: number }
  | { t: 'hero'; id: HeroId }
  | { t: 'cast'; k: 0 | 1 }
  | { t: 'buy'; item: ItemId }
  /** Buy the army's Extra: one ram out of the barracks (EXTRA's price, tier and cooldown). */
  | { t: 'extra' };
export type Ev =
  | { e: 'shot'; from: number; x0: number; y0: number; x1: number; y1: number; cls: number; team: number }
  | { e: 'hit'; x: number; y: number; cls: number; team: number }
  | { e: 'death'; i: number; x: number; y: number; cls: number; team: number }
  | { e: 'spawn'; i: number; team: number }
  | { e: 'baseHit'; p: number }
  | { e: 'tier'; p: number; tier: number; faction: FactionId | null }
  | { e: 'out'; p: number }
  | { e: 'win'; p: number }
  | { e: 'phase'; name: 'bases-open' | 'final' }
  | { e: 'hero'; p: number; i: number } // a hero takes the field (first time or revived)
  | { e: 'heroDown'; p: number; by: number; x: number; y: number } // by: the killing player, -1 for a tower
  | { e: 'level'; p: number; level: number }
  | { e: 'cast'; p: number; k: number; x0: number; y0: number; x: number; y: number; r: number }
  | { e: 'buy'; p: number; item: ItemId }
  | { e: 'extra'; p: number; i: number }
  | { e: 'boss'; wave: number; i: number; grew: boolean } // the giant wakes, or (grew) still standing grows to the next wave
  | { e: 'bossDown'; p: number; gold: number; x: number; y: number } // p: the slayer's team, -1 for none
  | { e: 'slam'; x: number; y: number; r: number }; // the giant's strike lands

/** A hero's skill on the ground for a while: an arrow storm, Joan's relief, Leonidas's pass, Wallace's Stirling or
 *  Lagertha's spin. It pulses every `every` seconds while its caster lives; `follow` zones move with the caster. */
export type ZoneKind = 'storm' | 'relief' | 'pass' | 'stirling' | 'spin';
export type Zone = { p: number; src: number; kind: ZoneKind; x: number; y: number; r: number; m: number; every: number; until: number; next: number; follow: boolean };

/** A goal some of a player's units follow instead of the army's; units share one when ordered together. */
export type Order = { p: number; goal: Goal; field: FlowField; move: boolean };

export type HeroState = {
  /** The group the hero was in when it fell, which it rejoins when it revives (-1 none). */
  squad: number;
  id: HeroId; i: number; level: number; xp: number; mana: number; ready: [number, number]; respawn: number;
  whet: number; stack: number; deaths: number;
  /** Cuauhtemoc's Last Stand runs until this time; Lagertha's strikes, for Shield Bash. */
  stand: number; bash: number;
};

export type Player = {
  gold: number; tier: number; faction: FactionId | null; slots: ClassId[]; slotT: number[]; slotLock: number[];
  baseHp: number; baseMax: number; alive: number; out: boolean; kills: number; goal: Goal; field: FlowField;
  /** Gold from kills this match (the end card; gold now leaves by tiers and the shop). */
  earned: number;
  hero: HeroState | null;
  /** Until when the Giant's Drum (the boss's reward) and Liang Hongyu's River Drums run for this player's units. */
  might: number; drums: number;
  /** Each group's order (an index in Sim.orders, -1 the army goal): what recruits joining the group follow. */
  squads: number[];
  /** The group new soldiers join, or -1 the army. */
  rally: number;
  /** When the next Extra can be bought. */
  extraAt: number;
};

export class Sim {
  readonly rng: Rng;
  readonly blocked = new Uint8Array(SIZE * SIZE);
  /** 1 on road cells, for the renderer (roads are the open ground the forest was carved from). */
  readonly road = new Uint8Array(SIZE * SIZE);
  readonly bases = [{ x: 18, y: 18 }, { x: SIZE - 18, y: 18 }, { x: SIZE - 18, y: SIZE - 18 }, { x: 18, y: SIZE - 18 }];
  readonly center = { x: SIZE / 2, y: SIZE / 2 };
  readonly players: Player[] = [];
  readonly centerField: FlowField;
  readonly baseFields: FlowField[] = [];

  // units
  readonly x = new Float32Array(CAP); readonly y = new Float32Array(CAP);
  readonly hp = new Float32Array(CAP); readonly maxHp = new Float32Array(CAP);
  readonly dmg = new Float32Array(CAP); readonly cd = new Float32Array(CAP);
  readonly range = new Float32Array(CAP); readonly speed = new Float32Array(CAP);
  readonly cls = new Uint8Array(CAP); readonly team = new Uint8Array(CAP); readonly rank = new Uint8Array(CAP);
  readonly state = new Uint8Array(CAP); readonly target = new Int32Array(CAP).fill(-1);
  readonly baseTarget = new Int8Array(CAP).fill(-1);
  readonly think = new Float32Array(CAP); readonly face = new Float32Array(CAP); readonly phase = new Float32Array(CAP);
  readonly lastHit = new Int32Array(CAP).fill(-1); readonly kills = new Uint8Array(CAP);
  readonly calm = new Float32Array(CAP); readonly charge = new Uint8Array(CAP);
  readonly buff = new Uint8Array(CAP); readonly near = new Uint8Array(CAP);
  /** Seconds between strikes (the class's, or the hero's). */
  readonly rate = new Float32Array(CAP);
  /** The player whose hero this unit is, else -1. */
  readonly heroOf = new Int8Array(CAP).fill(-1);
  /** The unit a hero is locked in a duel with, else -1. */
  readonly duel = new Int32Array(CAP).fill(-1);
  duels: { a: number; b: number; until: number; x: number; y: number }[] = [];
  zones: Zone[] = [];
  /** The spot a neutral holds: a camp's team (0 to 3), PLAYERS for the giant, else -1 (the hard end's waves march). */
  readonly post = new Int8Array(CAP).fill(-1);
  /** Body radius: the class's, or the giant's. Reach and area checks measure to a big body's edge. */
  readonly rad = new Float32Array(CAP);
  /** Hero effects on a unit: damage taken times guardM until guardT, stunned until stunT, half speed until slowT. */
  readonly guardT = new Float32Array(CAP); readonly guardM = new Float32Array(CAP).fill(1);
  readonly stunT = new Float32Array(CAP); readonly slowT = new Float32Array(CAP);
  /** Until when another unit's aura hurries (support, Joan) or quickens (Liang) unit i. An end time, not a bit in buff:
   *  each unit rewrites its own buff (Volley) at its own think, which wiped an aura set by a unit that thought first
   *  (Astra 2026-10-26 F2). */
  readonly hasteT = new Float32Array(CAP); readonly quickT = new Float32Array(CAP);
  /** Salve seconds left and HP per second, fixed when bought and cancelled by a hit. */
  readonly salveT = new Float32Array(CAP); readonly salveRate = new Float32Array(CAP);
  /** Who a unit is, a new number every spawn: commands name units by it. */
  readonly uid = new Int32Array(CAP);
  /** The unit's own order (an index in orders), or -1: it follows its player's goal. */
  readonly ord = new Int16Array(CAP).fill(-1);
  /** The unit's group (0 to GROUPS - 1), or -1. */
  readonly squad = new Int8Array(CAP).fill(-1);
  /** 1 while a move order walks the unit past fights; cleared when it gets there. */
  readonly moving = new Uint8Array(CAP);
  /** Tight while moving; expands when settling beside an arrived ally on the same order. */
  readonly moveArrive = new Float32Array(CAP);
  /** Arrived on this order; stale idle state cannot anchor a newly issued move. */
  readonly moveSettled = new Uint8Array(CAP);
  /** Deadline bounds passive movement even when bodies or terrain prevent arrival. */
  readonly moveUntil = new Float64Array(CAP);
  /** Every player's live orders; a slot no unit or group uses is free for the next one. */
  orders: (Order | null)[] = [];
  private serial = 0;
  /** The giant's unit, -1 while none stands; waves woken so far. */
  boss = -1; bossWave = 0;

  private head = new Int32Array(HW * HW).fill(-1); // empty until the first step (a cast at 0:00 walks it)
  private next = new Int32Array(CAP);
  time = 0; ticks = 0; winner = -1; neutralAlive = 0;
  /** What happened in the last step, plus any commands taken since it (the client and the harness read these). */
  events: Ev[] = [];
  private pending: Ev[] = [];
  private towerT = new Float32Array(PLAYERS);
  private waveT = 0; private openSaid = false; private finalSaid = false;
  private stepping = false;
  /** Events raised inside a step go to this step's list; a command's (between steps) to the next one. */
  private emit(ev: Ev) { (this.stepping ? this.events : this.pending).push(ev); }

  constructor(seed = 1) {
    this.rng = new Rng(seed);
    this.buildMap();
    const c = this.center;
    this.centerField = this.flow([[c.x - 5, c.y - 5, 10, 10]]);
    for (const b of this.bases) this.baseFields.push(this.flow([[b.x - 4, b.y - 4, 8, 8]]));
    for (let p = 0; p < PLAYERS; p++) {
      this.players.push({ gold: ECON.startGold, tier: 0, faction: null, slots: ['footman'], slotT: [2 + p * 0.25], slotLock: [0],
        baseHp: BASE.hp, baseMax: BASE.hp, alive: 0, out: false, kills: 0, goal: { kind: 'center' }, field: this.centerField,
        earned: 0, hero: null, might: 0, drums: 0, squads: new Array(GROUPS).fill(-1), rally: -1, extraAt: 0 });
    }
  }

  // ---- map: a forest with the roads carved out. Four roads run from the corners to the center (the four paths in) and
  // four more along the edges join the corners, so every base has a front door and two flanks.
  private buildMap() {
    this.blocked.fill(1);
    const carve = (x0: number, y0: number, x1: number, y1: number, r: number) => {
      const n = Math.ceil(hypot(x1 - x0, y1 - y0));
      for (let s = 0; s <= n; s++) {
        const cx = x0 + ((x1 - x0) * s) / n, cy = y0 + ((y1 - y0) * s) / n;
        const rr = r + sin(s * 0.37 + x0) * 0.8; // a little wobble so roads look walked, not ruled
        for (let j = Math.floor(cy - rr); j <= cy + rr; j++) for (let i = Math.floor(cx - rr); i <= cx + rr; i++) {
          if (i < 1 || j < 1 || i >= SIZE - 1 || j >= SIZE - 1) continue;
          if (sq(i - cx) + sq(j - cy) <= rr * rr) { this.blocked[j * SIZE + i] = 0; this.road[j * SIZE + i] = 1; }
        }
      }
    };
    const c = this.center;
    for (const b of this.bases) { carve(b.x, b.y, b.x, b.y, 13); carve(b.x, b.y, c.x, c.y, 5); }
    for (let k = 0; k < 4; k++) { const a = this.bases[k], b = this.bases[(k + 1) % 4]; carve(a.x, a.y, b.x, b.y, 4.2); }
    // Carve a disk directly: the old zero-length path divided by zero and carved no plaza.
    // The board reads this same road mask.
    for (let y = 0; y < SIZE; y++) for (let x = 0; x < SIZE; x++) {
      if (hypot(x + 0.5 - c.x, y + 0.5 - c.y) <= PLAZA_RADIUS) {
        this.blocked[y * SIZE + x] = 0; this.road[y * SIZE + x] = 1;
      }
    }
    // the center tower itself and the barracks are solid
    const solid = (x: number, y: number, r: number) => {
      for (let j = Math.floor(y - r); j <= y + r; j++) for (let i = Math.floor(x - r); i <= x + r; i++)
        if (sq(i + 0.5 - x) + sq(j + 0.5 - y) <= r * r) this.blocked[j * SIZE + i] = 1;
    };
    solid(c.x, c.y, TOWER_RADIUS);
    for (const b of this.bases) solid(b.x, b.y, BASE.radius - 0.4);
  }

  /** BFS-Dijkstra from goal cells (8 neighbours, no corner cutting); every cell points at its cheapest neighbour. One
   *  field per goal, shared by every unit headed there. Blocked goal cells (a barracks) still seed the search. */
  flow(goals: [number, number, number, number][]): FlowField {
    const N = SIZE * SIZE, dist = new Float32Array(N).fill(Infinity);
    const q = new Int32Array(N * 4); let qh = 0, qt = 0;
    for (const [x, y, w, h] of goals) for (let j = Math.max(0, Math.floor(y)); j < Math.min(SIZE, y + h); j++)
      for (let i = Math.max(0, Math.floor(x)); i < Math.min(SIZE, x + w); i++) { const k = j * SIZE + i; if (this.blocked[k]) continue; dist[k] = 0; q[qt++] = k; }
    const D = [[1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1], [1, 1, Math.SQRT2], [1, -1, Math.SQRT2], [-1, 1, Math.SQRT2], [-1, -1, Math.SQRT2]];
    while (qh < qt) {
      const k = q[qh++], cx = k % SIZE, cy = (k / SIZE) | 0;
      for (const [ox, oy, w] of D) {
        const nx = cx + ox, ny = cy + oy;
        if (nx < 0 || ny < 0 || nx >= SIZE || ny >= SIZE) continue;
        const n = ny * SIZE + nx;
        if (this.blocked[n] || (ox && oy && (this.blocked[cy * SIZE + nx] || this.blocked[ny * SIZE + cx]))) continue;
        const d = dist[k] + w;
        if (d < dist[n] - 1e-6) { dist[n] = d; if (qt < q.length) q[qt++] = n; }
      }
    }
    const dx = new Float32Array(N), dy = new Float32Array(N);
    for (let k = 0; k < N; k++) {
      if (this.blocked[k] || !isFinite(dist[k]) || dist[k] === 0) continue;
      const cx = k % SIZE, cy = (k / SIZE) | 0;
      let best = dist[k], bx = 0, by = 0;
      for (const [ox, oy] of D) {
        const nx = cx + ox, ny = cy + oy;
        if (nx < 0 || ny < 0 || nx >= SIZE || ny >= SIZE) continue;
        const n = ny * SIZE + nx;
        if (this.blocked[n] || (ox && oy && (this.blocked[cy * SIZE + nx] || this.blocked[ny * SIZE + cx]))) continue;
        if (dist[n] < best) { best = dist[n]; bx = ox; by = oy; }
      }
      const l = hypot(bx, by) || 1;
      dx[k] = bx / l; dy[k] = by / l;
    }
    return { dx, dy, dist };
  }

  faction(p: number): FactionDef | null { const f = this.players[p]?.faction; return f ? FACTION[f] : null; }

  // ---- commands (the client, the bots and later the server all go through here)
  canTier(p: number) {
    const pl = this.players[p];
    if (pl.out || pl.tier >= 4) return false;
    return this.time >= TIER_GATE[pl.tier] && pl.gold >= TIER_COST[pl.tier];
  }
  command(p: number, c: Command) {
    const pl = this.players[p];
    if (!pl || pl.out || this.winner >= 0) return false;
    if (c.t === 'tier') {
      if (!this.canTier(p)) return false;
      if (pl.tier === 0) { if (!c.faction || !FACTIONS.includes(c.faction)) return false; pl.faction = c.faction; }
      pl.gold -= TIER_COST[pl.tier]; pl.tier++;
      pl.baseMax += BASE.hpPerTier; pl.baseHp += BASE.hpPerTier;
      const defaults: ClassId[] = ['line', 'ranged', 'spear', 'cavalry', 'elite'];
      if (pl.tier === 1) pl.slots[0] = 'line';
      while (pl.slots.length < SLOTS[pl.tier]) {
        const open = defaults.filter((d) => CLASS[d].tier <= pl.tier && this.slotRoom(pl, d));
        const pick = open.find((d) => !pl.slots.includes(d)) ?? open[0] ?? 'line'; // a new slot adds a class you don't have yet
        pl.slots.push(pick); pl.slotT.push(CLASS[pick].interval * 0.5); pl.slotLock.push(0);
      }
      this.emit({ e: 'tier', p, tier: pl.tier, faction: pl.faction });
      return true;
    }
    if (c.t === 'slot') {
      if (c.i < 0 || c.i >= pl.slots.length || pl.tier === 0) return false;
      if (CLASS[c.cls].tier > pl.tier || CLASS[c.cls].maxSlots < 1 || c.cls === 'footman' || pl.slotLock[c.i] > this.time) return false;
      if (pl.slots[c.i] === c.cls) return true;
      const others = pl.slots.filter((s, k) => k !== c.i && s === c.cls).length;
      if (others >= CLASS[c.cls].maxSlots) return false;
      pl.slots[c.i] = c.cls; pl.slotLock[c.i] = this.time + 10;
      return true;
    }
    if (c.t === 'goal') {
      const field = this.fieldFor(c.goal);
      if (!field) return false;
      pl.field = field; pl.goal = c.goal.kind === 'point' ? { kind: 'point', ...this.openNear(c.goal.x, c.goal.y) } : c.goal;
      // the army is everyone outside a group: soldiers sent off on their own come back to it
      for (let i = 0; i < CAP; i++) if (this.ord[i] >= 0 && this.squad[i] < 0 && this.team[i] === p && this.state[i] !== STATE.dead) { this.ord[i] = -1; this.moving[i] = 0; }
      for (let i = 0; i < CAP; i++) if (this.ord[i] < 0 && this.team[i] === p) { this.moveArrive[i] = 0.25; this.moveSettled[i] = 0; }
      // falling back (2026-11-01, Devin: soldiers told to retreat stayed locked in their fight): the army walks home past
      // the fight, as a move order does, and fights again once clear of it or home
      if (c.back && c.goal.kind === 'base' && c.goal.p === p) {
        for (let i = 0; i < CAP; i++) {
          if (this.ord[i] >= 0 || this.team[i] !== p || this.state[i] === STATE.dead) continue;
          const d = field.dist[(this.y[i] | 0) * SIZE + (this.x[i] | 0)];
          this.moving[i] = 1;
          this.moveUntil[i] = this.time + Math.min(120, 2 + (Number.isFinite(d) ? d : 20) / Math.max(0.5, this.speed[i]) * 3);
          if (this.duel[i] >= 0) this.endDuel(i);
          this.target[i] = -1; this.baseTarget[i] = -1;
        }
      }
      return true;
    }
    if (c.t === 'order') return this.order(p, c.ids, c.goal, !!c.move, c.tap);
    if (c.t === 'group') return this.group(p, c.k, c.ids, !!c.add);
    if (c.t === 'rally') {
      if (!Number.isInteger(c.k) || c.k < -1 || c.k >= GROUPS) return false;
      pl.rally = c.k;
      return true;
    }
    if (c.t === 'hero') {
      if (pl.hero || !HEROES.includes(c.id)) return false;
      pl.hero = { squad: -1, id: c.id, i: -1, level: 1, xp: 0, mana: 0, ready: [0, 0], respawn: this.time, whet: 0, stack: 0, deaths: 0, stand: 0, bash: 0 };
      this.spawnHero(p); // with the field full it waits for room, like a revive
      return true;
    }
    if (c.t === 'cast') return this.cast(p, c.k);
    if (c.t === 'buy') return this.buy(p, c.item);
    if (c.t === 'extra') {
      if (!this.canExtra(p)) return false;
      const { x, y } = this.gate(p), i = this.spawnUnit(p, 'ram', pl.tier, x, y);
      if (i < 0) return false;
      pl.gold -= EXTRA.cost; pl.extraAt = this.time + EXTRA.cooldown;
      this.emit({ e: 'extra', p, i });
      return true;
    }
    return false;
  }
  /** Whether player p could buy its army's Extra now: an army with a ram, the tier, the gold, the cooldown, room. */
  canExtra(p: number) {
    const pl = this.players[p], f = this.faction(p);
    return !!f?.ram && !pl.out && pl.tier >= EXTRA.tier && pl.gold >= EXTRA.cost && this.time >= pl.extraAt && pl.alive < ECON.cap;
  }
  /** The flow field to a goal (the shared center and base ones, a new one for a point), or null for a goal that isn't. */
  private fieldFor(g: Goal): FlowField | null {
    if (!g) return null;
    if (g.kind === 'center') return this.centerField;
    if (g.kind === 'base') return Number.isInteger(g.p) && g.p >= 0 && g.p < PLAYERS ? this.baseFields[g.p] : null;
    if (g.kind !== 'point' || !Number.isFinite(g.x) || !Number.isFinite(g.y)) return null;
    const { x, y } = this.openNear(g.x, g.y);
    return this.flow([[x - 2, y - 2, 4, 4]]);
  }
  /** The open ground nearest a point (inside the map's edge): a goal in the forest would seed no cell, and a move order
   *  to it would walk forever without fighting. */
  openNear(px: number, py: number) {
    let x = Math.max(2, Math.min(SIZE - 3, px)), y = Math.max(2, Math.min(SIZE - 3, py));
    const ox = x - this.center.x, oy = y - this.center.y, d = hypot(ox, oy), clearance = PLINTH_RADIUS + 0.8 + 0.1;
    if (d < clearance) { x = this.center.x + (d ? ox / d : 1) * clearance; y = this.center.y + (d ? oy / d : 0) * clearance; }
    for (let r = 0; r < SIZE; r++) {
      let best: { x: number; y: number } | null = null, bd = Infinity;
      for (let j = Math.floor(y) - r; j <= Math.floor(y) + r; j++) for (let i = Math.floor(x) - r; i <= Math.floor(x) + r; i++) {
        if (i < 1 || j < 1 || i >= SIZE - 1 || j >= SIZE - 1 || this.blocked[j * SIZE + i]) continue;
        if (Math.max(Math.abs(i - Math.floor(x)), Math.abs(j - Math.floor(y))) !== r) continue;
        const d = sq(i + 0.5 - x) + sq(j + 0.5 - y);
        if (hypot(i + 0.5 - this.center.x, j + 0.5 - this.center.y) < clearance) continue;
        if (d < bd) { bd = d; best = { x: r ? i + 0.5 : x, y: r ? j + 0.5 : y }; }
      }
      if (best) return best;
    }
    return { x, y };
  }
  /** Player p's living units named in ids (uids), each once, in slot order. */
  private unitsOf(p: number, ids: number[]) {
    if (!Array.isArray(ids) || ids.length > CAP) return [];
    const want = new Set(ids.filter((u) => Number.isInteger(u) && u > 0)), out: number[] = [];
    for (let i = 0; i < CAP; i++) if (this.state[i] !== STATE.dead && this.team[i] === p && want.has(this.uid[i])) out.push(i);
    return out;
  }
  /** A new or revived unit joins its player's group q and takes up the group's order, a move one too (Astra F3). */
  private join(i: number, q: number) {
    const o = this.players[this.team[i]].squads[q];
    this.squad[i] = q; this.ord[i] = o; this.startMove(i);
  }
  /** The goal unit i follows: its own order's, else its player's. */
  goalOf(i: number): Goal { const o = this.ord[i]; return o >= 0 ? this.orders[o]!.goal : this.players[this.team[i]].goal; }
  /** An order slot for player p to goal g: a live one with the same goal shared, else a free slot (one no living unit or
   *  group points at), else -1 when the player already has ORDER_CAP. */
  private orderSlot(p: number, g: Goal, move: boolean, leaving: Set<number> = new Set(), groups: number[] = []) {
    const same = (a: Goal) => a.kind === g.kind && (a.kind !== 'base' || a.p === (g as { p: number }).p)
      && (a.kind !== 'point' || (a.x === (g as { x: number }).x && a.y === (g as { y: number }).y));
    // counted as after the order: the units it moves and the groups it takes over let go of theirs (Astra F1: twelve
    // orders, and moving one order's only soldier somewhere new was refused)
    const used = new Uint8Array(this.orders.length + 1);
    for (let i = 0; i < CAP; i++) if (this.ord[i] >= 0 && this.state[i] !== STATE.dead && !leaving.has(i)) used[this.ord[i]] = 1;
    this.players.forEach((pl, q) => pl.squads.forEach((o, k) => { if (o >= 0 && !(q === p && groups.includes(k))) used[o] = 1; }));
    let free = -1, mine = 0;
    for (let k = 0; k < this.orders.length; k++) {
      const o = this.orders[k];
      if (!used[k]) { if (free < 0) free = k; continue; }
      if (o!.p !== p) continue;
      mine++;
      if (o!.move === move && same(o!.goal)) return k;
    }
    if (mine >= ORDER_CAP) return -1;
    const field = g.kind === 'point' ? this.flow([[Math.floor(g.x), Math.floor(g.y), 1, 1]]) : this.fieldFor(g);
    if (!field) return -1;
    const o: Order = { p, goal: g.kind === 'point' ? { kind: 'point', x: g.x, y: g.y } : g, field, move };
    if (free < 0) { this.orders.push(o); return this.orders.length - 1; }
    this.orders[free] = o;
    return free;
  }
  /** Read-only selection query; never changes the goal kind. */
  retreat(p: number, ids: number[], g: Goal): boolean {
    return g.kind === 'point' && this.unitsOf(p, ids).some(i => this.retreatUnit(i, g));
  }
  private retreatUnit(i: number, at: { x: number; y: number }) {
    const attackers: { x: number; y: number }[] = [];
    let onEnemy = false;
    for (let j = 0; j < CAP; j++) {
      if (this.state[j] === STATE.dead || this.team[j] === this.team[i]) continue;
      if (hypot(at.x - this.x[j], at.y - this.y[j]) < this.rad[j] + 1.2) onEnemy = true;
      if (this.target[i] === j || this.target[j] === i || this.duel[i] === j)
        attackers.push({ x: this.x[j], y: this.y[j] });
    }
    const base = this.baseTarget[i];
    if (base >= 0) attackers.push(this.bases[base]);
    const o = this.ord[i] >= 0 ? this.orders[this.ord[i]] : null;
    return retreatTap({ x: this.x[i], y: this.y[i] }, at, attackers,
      !!this.moving[i] && !o?.move && this.time < this.moveUntil[i], onEnemy);
  }
  private startMove(i: number, disengage = false) {
    const o = this.ord[i] >= 0 ? this.orders[this.ord[i]] : null;
    this.moving[i] = o?.move || disengage ? 1 : 0;
    const g = o?.goal;
    const d = g?.kind === 'point' ? hypot(g.x - this.x[i], g.y - this.y[i]) : o?.field.dist[(this.y[i] | 0) * SIZE + (this.x[i] | 0)] ?? 0;
    this.moveSettled[i] = 0;
    this.moveArrive[i] = Math.min(2.5, Math.max(0.15, d * 0.2));
    this.moveUntil[i] = this.time + (disengage ? 2.5 : Math.min(120, 2 + d / Math.max(0.5, this.speed[i]) * 3));
  }
  private order(p: number, ids: number[], g: Goal | null, move: boolean, tap?: { x: number; y: number }) {
    const us = this.unitsOf(p, ids);
    if (!us.length) return false;
    if (g && (g.kind === 'point' ? !Number.isFinite(g.x) || !Number.isFinite(g.y) : g.kind === 'base' ? !Number.isInteger(g.p) || g.p < 0 || g.p >= PLAYERS : g.kind !== 'center')) return false;
    if (tap && (!Number.isFinite(tap.x) || !Number.isFinite(tap.y))) return false;
    // Decide from the raw plain-point tap before navigation projects it onto open ground.
    const rawTap = tap ?? (g?.kind === 'point' ? g : null);
    const disengage = new Set(g?.kind === 'point' && rawTap && !move
      ? us.filter(i => this.retreatUnit(i, rawTap)) : []);
    if (g?.kind === 'point') g = { kind: 'point', ...this.openNear(g.x, g.y) };
    // a group every living member of which gets this order takes it: its recruits follow it from now on
    const sel = new Set(us), pl = this.players[p], whole: number[] = [];
    for (let q = 0; q < GROUPS; q++) {
      let n = 0, all = true;
      for (let i = 0; i < CAP && all; i++) if (this.squad[i] === q && this.team[i] === p && this.state[i] !== STATE.dead) { n++; all = sel.has(i); }
      if (n && all) whole.push(q);
    }
    const k = g ? this.orderSlot(p, g, move, sel, whole) : -1;
    if (g && k < 0) return false;
    for (const i of us) {
      this.ord[i] = k; this.startMove(i, disengage.has(i));
      if (this.moving[i] && this.duel[i] >= 0) this.endDuel(i);
      this.target[i] = -1; this.baseTarget[i] = -1;
    }
    for (const q of whole) pl.squads[q] = k;
    return true;
  }
  private group(p: number, q: number, ids: number[], add: boolean) {
    if (!Number.isInteger(q) || q < 0 || q >= GROUPS) return false;
    const us = this.unitsOf(p, ids);
    if (!us.length) return false;
    if (!add) {
      for (let i = 0; i < CAP; i++) if (this.squad[i] === q && this.team[i] === p) this.squad[i] = -1;
      const h = this.players[p].hero;
      if (h && h.i < 0 && h.squad === q) h.squad = -1; // a fallen hero left out of the new group doesn't revive into it
    }
    for (const i of us) this.squad[i] = q;
    // the group's order is the one its members share; members on different orders give it none (the army's)
    let o = -2;
    for (let i = 0; i < CAP; i++) if (this.squad[i] === q && this.team[i] === p && this.state[i] !== STATE.dead) o = o === -2 || o === this.ord[i] ? this.ord[i] : -1;
    this.players[p].squads[q] = Math.max(-1, o);
    return true;
  }
  private slotRoom(pl: Player, c: ClassId) { return pl.slots.filter((s) => s === c).length < CLASS[c].maxSlots; }

  /** Where a barracks' soldiers come out: the gate facing the center. */
  gate(p: number) {
    const b = this.bases[p];
    return { x: b.x + Math.sign(this.center.x - b.x) * 4.5, y: b.y + Math.sign(this.center.y - b.y) * 4.5 };
  }

  // ---- heroes
  private spawnHero(p: number) {
    const h = this.players[p].hero!, g = this.gate(p), d = HERO[h.id];
    const i = this.spawnUnit(p, 'hero', 0, g.x, g.y);
    if (i < 0) return;
    const st = heroStats(h.id, h.level, h.whet);
    this.heroOf[i] = p; this.hp[i] = this.maxHp[i] = st.hp; this.dmg[i] = st.dmg;
    this.rate[i] = d.cd; this.range[i] = d.range; this.speed[i] = d.speed;
    h.i = i; h.mana = st.mana; h.stack = 0;
    if (h.squad >= 0) this.join(i, h.squad); // back to its group
    this.emit({ e: 'hero', p, i });
  }
  /** A hero is killed: it revives at its barracks after a wait that grows with its level. */
  private heroDown(p: number, by: number) {
    const h = this.players[p].hero!, i = h.i;
    this.endDuel(i);
    this.zones = this.zones.filter((z) => z.src !== i);
    h.squad = this.squad[i]; h.i = -1; h.stand = 0; h.respawn = this.time + HR.revive(h.level); h.deaths++;
    this.emit({ e: 'heroDown', p, by: by >= 0 && this.team[by] < PLAYERS ? this.team[by] : -1, x: this.x[i], y: this.y[i] });
  }
  /** XP for player k's hero when one of its side kills unit `dead`: the hero has to be the killer or near the kill. */
  private giveXp(k: number, dead: number, by: number, xp: number) {
    const h = this.players[k].hero;
    if (!h || h.i < 0 || h.level >= HR.maxLevel) return;
    if (h.i !== by && hypot(this.x[h.i] - this.x[dead], this.y[h.i] - this.y[dead]) > HR.xpRadius) return;
    h.xp += xp;
    while (h.level < HR.maxLevel && h.xp >= HR.xpToNext(h.level)) {
      h.xp -= HR.xpToNext(h.level); h.level++;
      const st = heroStats(h.id, h.level, h.whet);
      this.hp[h.i] += st.hp - this.maxHp[h.i]; this.maxHp[h.i] = st.hp; this.dmg[h.i] = st.dmg;
      this.emit({ e: 'level', p: k, level: h.level });
    }
    if (h.level >= HR.maxLevel) h.xp = 0;
  }
  /** Mana the hero gets a second: fast near the center. */
  manaRate(p: number) {
    const h = this.players[p].hero;
    if (!h || h.i < 0) return 0;
    return hypot(this.x[h.i] - this.center.x, this.y[h.i] - this.center.y) <= HR.centerRadius ? HR.centerRegen : HR.manaRegen;
  }
  outOfCombat(i: number) {
    if (this.calm[i] < HR.calmAfter || this.target[i] >= 0 || this.baseTarget[i] >= 0 || this.duel[i] >= 0) return false;
    for (let j = 0; j < CAP; j++) if (this.state[j] !== STATE.dead && this.team[j] !== this.team[i]
      && (this.target[j] === i || hypot(this.x[j] - this.x[i], this.y[j] - this.y[i]) <= Math.max(AGGRO + this.rad[j], this.range[j] + this.rad[i]))) return false;
    return true;
  }
  nearCenter(i: number) { return hypot(this.x[i] - this.center.x, this.y[i] - this.center.y) <= HR.centerRadius; }

  /** Whether player p's hero could cast skill k now (level, mana, cooldown; not whether a target is in reach). */
  canCast(p: number, k: 0 | 1) {
    const h = this.players[p]?.hero;
    if (!h || h.i < 0) return false;
    const sk = HERO[h.id].skills[k];
    return h.level >= sk.level && h.mana >= sk.mana && this.time >= h.ready[k] && this.stunT[h.i] <= this.time; // stunned: no casts (Astra F4)
  }
  /** First-skill readiness including its actual target rules, for the guide. */
  canCastFirstNow(p: number) {
    if (!this.canCast(p, 0)) return false;
    const h = this.players[p].hero!, i = h.i, r = HERO[h.id].skills[0].r ?? 0;
    // Reuse the cast target rules on a query view with its own spatial index.
    // Rendering must never refresh the simulation's possibly stale between-step buckets.
    const query: Sim = Object.create(this);
    query.head = new Int32Array(this.head.length);
    query.next = new Int32Array(this.next.length);
    query.rehash();
    switch (h.id) {
      case 'musashi': return query.foeHero(i, HR.dashRange) >= 0 || query.nearestFoe(i, HR.dashRange - 2) >= 0;
      case 'tomoe': return query.foes(this.x[i], this.y[i], HR.sweepRadius, this.team[i]).length > 0;
      case 'joan': return query.allies(this.x[i], this.y[i], r, this.team[i]).some(j => this.hp[j] < this.maxHp[j] * .85);
      case 'liang': return query.nearestFoe(i, AGGRO + 1) >= 0;
      case 'leonidas': case 'cuauhtemoc': return query.foeHero(i, r) >= 0 || query.nearestFoe(i, h.id === 'leonidas' ? r : r - 3) >= 0;
      case 'wallace': return query.foes(this.x[i], this.y[i], r, this.team[i]).length > 0;
      case 'lagertha': return query.thickest(i, r, 3) >= 0;
    }
  }
  private cast(p: number, k: 0 | 1) {
    if ((k !== 0 && k !== 1) || !this.canCast(p, k)) return false;
    const h = this.players[p].hero!, i = h.i, sk = HERO[h.id].skills[k], x0 = this.x[i], y0 = this.y[i], t = this.team[i];
    this.rehash(); // commands come between steps: a dash or a spawn since the last step moved units off their buckets (Astra F2)
    const r = sk.r ?? 0, hit = this.dmg[i] * (sk.hit ?? 1), until = this.time + (sk.time ?? 0);
    /** Where the cast shows (a ring of radius r), or null when it has nothing to do and costs nothing. */
    let fx: { x: number; y: number; r: number } | null = null;
    switch (`${h.id}${k}`) {
      case 'musashi0': {
        // Crossing Cut: dash to the nearest enemy hero in reach, else the nearest soldier, and strike
        let tg = this.foeHero(i, HR.dashRange);
        if (tg < 0) tg = this.nearestFoe(i, HR.dashRange - 2);
        if (tg < 0) break;
        this.dashTo(i, tg);
        fx = { x: this.x[i], y: this.y[i], r: 0 };
        this.damage(i, tg, this.dmg[i] * HR.dashHit * (this.heroOf[tg] >= 0 ? HR.dashHeroBonus : 1));
        break;
      }
      case 'musashi1': {
        // The Duel: lock the nearest enemy hero in a ring with him
        if (this.duel[i] >= 0) break;
        const tg = this.foeHero(i, HR.duelRange);
        if (tg < 0) break;
        const x = (x0 + this.x[tg]) / 2, y = (y0 + this.y[tg]) / 2;
        this.duels.push({ a: i, b: tg, until: this.time + HR.duelTime, x, y });
        this.duel[i] = tg; this.duel[tg] = i; this.target[i] = tg; this.target[tg] = i;
        fx = { x, y, r: HR.duelRing };
        break;
      }
      case 'tomoe0': {
        // Naginata Sweep: every foe close around her
        const hits = this.foes(x0, y0, HR.sweepRadius, t);
        if (!hits.length) break;
        for (const j of hits) this.damage(i, j, this.dmg[i] * HR.sweepHit);
        fx = { x: x0, y: y0, r: HR.sweepRadius };
        break;
      }
      case 'tomoe1': {
        // Arrow Storm: on the foe in reach with the most foes around it
        const best = this.thickest(i, HR.stormRange, HR.stormRadius);
        if (best < 0) break;
        fx = { x: this.x[best], y: this.y[best], r: HR.stormRadius };
        this.zone(p, i, 'storm', fx.x, fx.y, HR.stormRadius, HR.stormHit, HR.stormEvery, this.time + HR.stormTime, false);
        break;
      }
      case 'joan0': case 'joan1': {
        // Rally heals the allies around her at once; Relief of Orleans heals them over time. Either needs someone hurt.
        const near = this.allies(x0, y0, r, t);
        if (!near.some((j) => this.hp[j] < this.maxHp[j] * 0.85)) break;
        if (k === 0) for (const j of near) this.hp[j] = Math.min(this.maxHp[j], this.hp[j] + this.maxHp[j] * sk.m!);
        else this.zone(p, i, 'relief', x0, y0, r, sk.m!, HR.zoneEvery, until, false);
        fx = { x: x0, y: y0, r };
        break;
      }
      case 'liang0': {
        // Signal: the allies around her take less damage for a while, once a fight is near
        if (this.nearestFoe(i, AGGRO + 1) < 0) break;
        for (const j of this.allies(x0, y0, r, t)) this.ward(j, sk.m!, until);
        fx = { x: x0, y: y0, r };
        break;
      }
      case 'liang1': {
        // The River Drums: every one of her side's soldiers strikes faster and heals, anywhere on the map
        if (this.nearestFoe(i, 10) < 0) break;
        this.players[p].drums = until;
        fx = { x: x0, y: y0, r: 6 };
        break;
      }
      case 'leonidas0': case 'cuauhtemoc0': {
        // Kick: the nearest foe in reach flies back. Descending Eagle: a leap onto the nearest hero (or soldier), and a stun.
        let tg = this.foeHero(i, r);
        if (tg < 0) tg = this.nearestFoe(i, h.id === 'leonidas' ? r : r - 3);
        if (tg < 0) break;
        if (h.id === 'cuauhtemoc') this.dashTo(i, tg, true);
        this.damage(i, tg, hit);
        if (this.state[tg] !== STATE.dead && this.canHurt(i, tg)) {
          if (h.id === 'leonidas') this.shove(tg, this.x[tg] - this.x[i], this.y[tg] - this.y[i], HR.kickPush);
          else this.stun(tg, sk.time!);
        }
        fx = { x: this.x[tg], y: this.y[tg], r: 1.2 };
        break;
      }
      case 'leonidas1': case 'wallace1': case 'lagertha1': {
        // Hold the Pass (a ring where he stands), Stirling and Shield Maiden's Storm (rings that move with the hero)
        if (this.nearestFoe(i, AGGRO + 1) < 0) break;
        if (h.id === 'leonidas') this.zone(p, i, 'pass', x0, y0, r, sk.m!, HR.zoneEvery, until, false);
        else if (h.id === 'wallace') this.zone(p, i, 'stirling', x0, y0, r, sk.m!, HR.stirlingEvery, until, true);
        else this.zone(p, i, 'spin', x0, y0, r, sk.hit!, HR.zoneEvery, until, true);
        fx = { x: x0, y: y0, r };
        break;
      }
      case 'wallace0': {
        // Claymore Sweep: every foe close by is hit and thrown back
        const hits = this.foes(x0, y0, r, t);
        if (!hits.length) break;
        for (const j of hits) {
          this.damage(i, j, hit);
          if (this.state[j] !== STATE.dead && this.canHurt(i, j)) this.shove(j, this.x[j] - x0, this.y[j] - y0, HR.sweepPush);
        }
        fx = { x: x0, y: y0, r };
        break;
      }
      case 'lagertha0': {
        // Spinning Charge: dash at the thickest knot of foes in reach, cutting every foe she passes
        const tg = this.thickest(i, r, 3);
        if (tg < 0) break;
        const cut = new Set<number>();
        this.dashTo(i, tg, false, (x, y) => { for (const j of this.foes(x, y, 1.8, t)) cut.add(j); });
        for (const j of cut) this.damage(i, j, hit);
        fx = { x: this.x[i], y: this.y[i], r: 1.8 };
        break;
      }
      case 'cuauhtemoc1': {
        // Last Stand: he can't fall for a while, and hits harder the closer he is to falling
        if (this.nearestFoe(i, AGGRO) < 0) break;
        h.stand = until;
        fx = { x: x0, y: y0, r: 1.5 };
        break;
      }
    }
    if (!fx) return false;
    h.mana -= sk.mana; h.ready[k] = this.time + sk.cd;
    this.emit({ e: 'cast', p, k, x0, y0, ...fx });
    return true;
  }
  /** Foes (or allies) of team t within r of a point, collected before anything is done to them. */
  private foes(x: number, y: number, r: number, t: number) { const a: number[] = []; this.each(x, y, r, (j) => { if (this.team[j] !== t) a.push(j); }); return a; }
  private allies(x: number, y: number, r: number, t: number) { const a: number[] = []; this.each(x, y, r, (j) => { if (this.team[j] === t) a.push(j); }); return a; }
  /** The foe within reach of unit i with the most foes within r of it (not one in someone else's duel), else -1. */
  private thickest(i: number, reach: number, r: number) {
    let best = -1, most = 0;
    const t = this.team[i];
    for (const j of this.foes(this.x[i], this.y[i], reach, t)) {
      if (this.duel[j] >= 0 && this.duel[j] !== i) continue;
      const n = this.foes(this.x[j], this.y[j], r, t).length;
      if (n > most) { most = n; best = j; }
    }
    return best;
  }
  /** Move hero i in a straight line to a step short of unit tg. A dash stops at the forest's edge; a leap (`over`) clears
   *  anything and lands on the open ground nearest the target. `each` sees every half step of a dash's path. */
  private dashTo(i: number, tg: number, over = false, each?: (x: number, y: number) => void) {
    const x0 = this.x[i], y0 = this.y[i], dx = this.x[tg] - x0, dy = this.y[tg] - y0, d = hypot(dx, dy) || 1e-6;
    const go = Math.max(0, d - 1), open = (u: number) => !this.blocked[((y0 + (dy / d) * u) | 0) * SIZE + ((x0 + (dx / d) * u) | 0)];
    if (over) { let u = go; while (u > 0 && !open(u)) u -= 0.5; u = Math.max(0, u); this.x[i] = x0 + (dx / d) * u; this.y[i] = y0 + (dy / d) * u; }
    else for (let u = 0.5; u <= go && open(u); u += 0.5) { this.x[i] = x0 + (dx / d) * u; this.y[i] = y0 + (dy / d) * u; each?.(this.x[i], this.y[i]); }
    each?.(this.x[i], this.y[i]);
    this.face[i] = atan2(dy, dx); this.target[i] = tg; this.lastHit[i] = tg;
  }
  /** Whether a can hurt j: a duel's two heroes only hurt, and are only hurt by, each other. */
  private canHurt(a: number, j: number) { return !((this.duel[j] >= 0 && this.duel[j] !== a) || (this.duel[a] >= 0 && this.duel[a] !== j)); }
  /** The giant can't be moved or stunned. */
  private heavy(j: number) { return this.post[j] === PLAYERS; }
  /** Move unit j up to `dist` along (dx, dy), stopping at the forest's edge or the tower. */
  private shove(j: number, dx: number, dy: number, dist: number) {
    const d = hypot(dx, dy);
    if (this.heavy(j) || d < 1e-6) return;
    for (let u = 0.5; u <= dist; u += 0.5) {
      const nx = this.x[j] + (dx / d) * 0.5, ny = this.y[j] + (dy / d) * 0.5;
      if (this.blocked[(ny | 0) * SIZE + (nx | 0)]) break;
      this.x[j] = nx; this.y[j] = ny;
    }
  }
  private stun(j: number, secs: number) { if (!this.heavy(j)) this.stunT[j] = Math.max(this.stunT[j], this.time + secs); }
  /** j takes m times damage until `until`; a stronger ward already on wins until it runs out. */
  private ward(j: number, m: number, until: number) {
    if (this.guardT[j] > this.time && this.guardM[j] < m) return;
    this.guardT[j] = this.guardM[j] === m ? Math.max(this.guardT[j], until) : until; this.guardM[j] = m;
  }
  private zone(p: number, src: number, kind: ZoneKind, x: number, y: number, r: number, m: number, every: number, until: number, follow: boolean) {
    this.zones.push({ p, src, kind, x, y, r, m, every, until, next: this.time, follow });
  }
  /** The nearest enemy hero within r that is free to fight (not in someone else's duel); in a duel, the other duelist. */
  private foeHero(i: number, r: number) {
    if (this.duel[i] >= 0) return this.duel[i];
    let best = -1, bd = r;
    for (const pl of this.players) {
      const j = pl.hero?.i ?? -1;
      if (j < 0 || this.team[j] === this.team[i] || this.duel[j] >= 0) continue;
      const d = hypot(this.x[j] - this.x[i], this.y[j] - this.y[i]);
      if (d <= bd) { bd = d; best = j; }
    }
    return best;
  }
  private endDuel(i: number) {
    const k = this.duels.findIndex((d) => d.a === i || d.b === i);
    if (k < 0) return;
    const d = this.duels[k];
    this.duel[d.a] = this.duel[d.b] = -1;
    this.duels.splice(k, 1);
  }
  /** Whether player p could buy item now: the hero at the center, the gold, and the item's own limits. buy() asks this,
   *  and so does the page before it sends a purchase online (its toast can't wait for the turn). */
  canBuy(p: number, item: ItemId) {
    const pl = this.players[p], h = pl?.hero;
    if (!h || h.i < 0 || !ITEMS.includes(item) || !this.nearCenter(h.i)) return false;
    const it = ITEM[item], owned = item === 'whetstone' ? h.whet : 0, i = h.i;
    return !(owned >= it.max || pl.gold < it.cost(owned) || (item === 'salve' && (this.hp[i] >= this.maxHp[i] || this.salveT[i] > 0)));
  }
  private buy(p: number, item: ItemId) {
    if (!this.canBuy(p, item)) return false;
    const pl = this.players[p], h = pl.hero!, i = h.i, cost = ITEM[item].cost(item === 'whetstone' ? h.whet : 0);
    pl.gold -= cost;
    if (item === 'salve') { this.salveT[i] = SALVE.duration; this.salveRate[i] = this.maxHp[i] * SALVE.rate; }
    else { h.whet++; this.dmg[i] = heroStats(h.id, h.level, h.whet).dmg; }
    this.emit({ e: 'buy', p, item });
    return true;
  }
  /** What a unit's strikes count as: its class's kind, or its hero's. */
  private kindOf(i: number): Kind {
    const hp = this.heroOf[i];
    return hp >= 0 ? HERO[this.players[hp].hero!.id].kind : classOf(CLASSES[this.cls[i]], this.faction(this.team[i])).kind;
  }
  /** Whether unit i's strikes fly (a shot the client draws) rather than land by hand: ranged, siege, and a hero whose
   *  strikes are pierce (a ram strikes by hand). */
  private shoots(i: number, cl: ClassId) {
    return cl === 'ranged' || cl === 'siege' || (this.heroOf[i] >= 0 && this.kindOf(i) === 'pierce');
  }

  // ---- units
  private spawnUnit(team: number, cls: ClassId, tier: number, x: number, y: number) {
    for (let i = 0; i < CAP; i++) {
      if (this.state[i] !== STATE.dead) continue;
      const f = team < PLAYERS ? this.faction(team) : null;
      const s = unitStats(cls, tier, f), c = classOf(cls, f);
      this.x[i] = x + this.rng.range(-1, 1); this.y[i] = y + this.rng.range(-1, 1);
      this.hp[i] = this.maxHp[i] = s.hp; this.dmg[i] = s.dmg; this.range[i] = s.range; this.speed[i] = s.speed;
      this.cls[i] = CLASSES.indexOf(cls); this.team[i] = team; this.rank[i] = tier; this.state[i] = STATE.walk;
      this.cd[i] = 0.5; this.target[i] = -1; this.baseTarget[i] = -1; this.think[i] = this.rng.range(0, THINK);
      this.phase[i] = this.rng.next() * 6.283; this.lastHit[i] = -1; this.kills[i] = 0; this.calm[i] = 0;
      this.charge[i] = cls === 'cavalry' ? 1 : 0; this.buff[i] = 0;
      this.rate[i] = c.cd; this.heroOf[i] = -1; this.duel[i] = -1;
      // a reused slot starts clean: no post, its class's body, no ward, stun or slow left from the unit before
      this.uid[i] = ++this.serial; this.ord[i] = -1; this.squad[i] = -1; this.moving[i] = 0;
      this.moveArrive[i] = 0.25; this.moveUntil[i] = 0; this.moveSettled[i] = 0;
      // a new soldier joins the group its player rallies to, and follows that group's order
      const pl = team < PLAYERS ? this.players[team] : null;
      if (pl && cls !== 'hero' && pl.rally >= 0) this.join(i, pl.rally);
      this.post[i] = -1; this.rad[i] = c.radius; this.guardT[i] = 0; this.guardM[i] = 1; this.stunT[i] = 0; this.slowT[i] = 0; this.hasteT[i] = 0; this.quickT[i] = 0; this.salveT[i] = 0; this.salveRate[i] = 0;
      if (cls === 'hero') { /* heroes don't count toward the army cap or its soldier count */ }
      else if (team < PLAYERS) this.players[team].alive++; else this.neutralAlive++;
      this.emit({ e: 'spawn', i, team });
      return i;
    }
    return -1;
  }

  private kill(i: number, by: number) {
    if (this.state[i] === STATE.dead) return;
    this.state[i] = STATE.dead; this.salveT[i] = 0;
    const t = this.team[i], owner = this.heroOf[i];
    // a slot is reused by the next spawn: no hero's Two Swords may carry over to whoever takes it (Astra F1)
    for (const pl of this.players) if (pl.hero && pl.hero.i >= 0 && this.lastHit[pl.hero.i] === i) { this.lastHit[pl.hero.i] = -1; pl.hero.stack = 0; }
    if (owner >= 0) this.heroDown(owner, by);
    else if (t < PLAYERS) this.players[t].alive--; else this.neutralAlive--;
    this.emit({ e: 'death', i, x: this.x[i], y: this.y[i], cls: this.cls[i], team: t });
    const giant = i === this.boss, grow = 1 + BOSS.growth * Math.max(0, this.bossWave - 1), slayer = by >= 0 && this.team[by] < PLAYERS ? this.team[by] : -1;
    if (giant) { // the slot may be reused from the next spawn on: the giant is gone now, whoever killed it
      this.boss = -1;
      if (slayer >= 0) this.players[slayer].might = this.time + BOSS.buff;
      this.emit({ e: 'bossDown', p: slayer, gold: slayer >= 0 ? Math.round(BOSS.bounty * grow) : 0, x: this.x[i], y: this.y[i] });
    }
    if (slayer >= 0) {
      const k = slayer, pl = this.players[k], level = owner >= 0 ? this.players[owner].hero!.level : 0;
      const gold = owner >= 0 ? HR.bounty(level) : giant ? Math.round(BOSS.bounty * grow)
        : ECON.bountyBase + ECON.bountyPerRank * this.rank[i] + (this.post[i] >= 0 ? Math.round(BOSS.campBounty * grow) : 0);
      pl.gold += gold; pl.earned += gold;
      pl.kills++;
      this.giveXp(k, i, by, owner >= 0 ? HR.heroXp(level) : giant ? BOSS.xp : HR.unitXp(this.rank[i]));
      const f = this.heroOf[by] >= 0 ? null : this.faction(k); // heroes stand outside their army's mechanic
      const deeds = f?.deeds;
      if (deeds && this.kills[by] < deeds.kills * deeds.ranks) {
        this.kills[by]++;
        if (this.kills[by] % deeds.kills === 0) { const g = 1 + deeds.gain; this.maxHp[by] *= g; this.hp[by] *= g; this.dmg[by] *= g; }
      }
    }
  }

  private rehash() {
    this.head.fill(-1);
    for (let i = 0; i < CAP; i++) {
      if (this.state[i] === STATE.dead) continue;
      const h = ((this.y[i] / HASH) | 0) * HW + ((this.x[i] / HASH) | 0);
      this.next[i] = this.head[h]; this.head[h] = i;
    }
  }
  private each(px: number, py: number, r: number, fn: (j: number, d2: number) => void) {
    const c0 = Math.max(0, ((px - r) / HASH) | 0), c1 = Math.min(HW - 1, ((px + r) / HASH) | 0);
    const r0 = Math.max(0, ((py - r) / HASH) | 0), r1 = Math.min(HW - 1, ((py + r) / HASH) | 0), rr = r * r;
    const g = this.boss;
    for (let cy = r0; cy <= r1; cy++) for (let cx = c0; cx <= c1; cx++)
      for (let j = this.head[cy * HW + cx]; j !== -1; j = this.next[j]) {
        if (this.state[j] === STATE.dead || j === g) continue;
        const d2 = sq(this.x[j] - px) + sq(this.y[j] - py);
        if (d2 <= rr) fn(j, d2);
      }
    // the giant is one big body on the tower: found by its edge, at its edge's distance, wherever the buckets put it
    if (g >= 0) { const d = hypot(this.x[g] - px, this.y[g] - py) - this.rad[g]; if (d <= r) fn(g, sq(Math.max(0, d))); }
  }
  /** The nearest enemy within r. A duelist sees only its duel partner, and no one else sees a duelist. */
  private nearestFoe(i: number, r: number, minR = 0) {
    if (this.duel[i] >= 0) return this.duel[i];
    const t = this.team[i]; let best = -1, bd = Infinity; const m2 = minR * minR;
    // the giant is a target only for a side whose army is sent to the center: one sent anywhere else walks past or away
    // from it, so a bot that pulls back from a giant it can't beat really pulls back (Astra 2026-10-26 F1)
    const pass = t < PLAYERS && this.goalOf(i).kind !== 'center' ? this.boss : -2;
    this.each(this.x[i], this.y[i], r, (j, d2) => { if (this.team[j] !== t && j !== pass && this.duel[j] < 0 && d2 >= m2 && d2 < bd) { bd = d2; best = j; } });
    return best;
  }
  /** An enemy base unit i can reach from here, or -1. */
  private nearBase(i: number, r: number) {
    const t = this.team[i];
    for (let p = 0; p < PLAYERS; p++) {
      if (p === t || this.players[p].out) continue;
      const b = this.bases[p];
      if (hypot(b.x - this.x[i], b.y - this.y[i]) <= BASE.radius + r + 0.6) return p;
    }
    return -1;
  }

  private damage(a: number, j: number, amount: number) {
    // a duel's ring: its two heroes only hurt, and are only hurt by, each other
    if (!this.canHurt(a, j)) return;
    const aCls = CLASSES[this.cls[a]], dCls = CLASSES[this.cls[j]], kind = this.kindOf(a);
    const ta = this.team[a], tj = this.team[j];
    const fa = ta < PLAYERS && this.heroOf[a] < 0 ? this.faction(ta) : null, fj = tj < PLAYERS && this.heroOf[j] < 0 ? this.faction(tj) : null;
    let m = classBonus(aCls, dCls, fj?.eliteCounters ?? [], !!fa?.eliteTakesSpearBonus) * armorTaken(dCls, kind);
    if (kind === 'pierce' && fj?.pierceTaken) m *= fj.pierceTaken;
    if (kind === 'melee' && fj?.phalanx && this.near[j] >= fj.phalanx.near && ['footman', 'line', 'spear', 'elite'].includes(dCls)) m *= fj.phalanx.taken;
    if (fa?.fury) m *= 1 + fa.fury * (1 - this.hp[a] / this.maxHp[a]);
    m *= this.power(ta);
    // the heroes' own: Cuauhtemoc's Eagle's Eye and Last Stand, Wallace's Hold the Bridge
    const ha = this.heroOf[a] >= 0 ? this.players[this.heroOf[a]].hero! : null, hj = this.heroOf[j] >= 0 ? this.players[this.heroOf[j]].hero! : null;
    if (ha?.id === 'cuauhtemoc') {
      if (hj) m *= HERO.cuauhtemoc.passive.m!;
      if (ha.stand > this.time) m *= 1 + HERO.cuauhtemoc.skills[1].m! * (1 - this.hp[a] / this.maxHp[a]);
    }
    if (hj?.id === 'wallace' && this.state[j] !== STATE.walk) m *= HERO.wallace.passive.m!;
    this.hurt(j, amount * m, a);
  }
  /** Damage that has its multipliers: wards cut it, and a hero in its Last Stand stops at 1 HP. Towers hit through here
   *  too, so every source honors both. */
  private hurt(j: number, amount: number, by: number) {
    if (this.guardT[j] > this.time) amount *= this.guardM[j];
    this.hp[j] -= amount;
    this.calm[j] = 0; this.salveT[j] = 0;
    if (this.hp[j] > 0) return;
    const o = this.heroOf[j];
    if (o >= 0 && this.players[o].hero!.stand > this.time) { this.hp[j] = 1; return; }
    this.kill(j, by);
  }
  /** Damage times this for team t's units while the Giant's Drum (the boss's reward) runs. */
  private power(t: number) { return t < PLAYERS && this.players[t].might > this.time ? 1 + BOSS.might : 1; }
  /** Seconds until unit i strikes again: its rate, faster in a Volley or by Liang Hongyu's Battle Drum (the stronger of the
   *  two, never both), and her River Drums on top. Every number is the one the cards print (defs.ts; tests/numbers.test.ts). */
  private rateOf(i: number) {
    const t = this.team[i], volley = this.buff[i] & 2 ? this.faction(t)?.volley?.rate ?? 1 : 1;
    const drum = this.quickT[i] > this.time ? HERO.liang.passive.m! : 1;
    return this.rate[i] * Math.min(volley, drum) * (t < PLAYERS && this.players[t].drums > this.time ? HERO.liang.skills[1].m! : 1);
  }

  step() {
    if (this.winner >= 0) return;
    this.events = this.pending; this.pending = [];
    this.stepping = true;
    this.ticks++; this.time += DT;
    if (!this.openSaid && this.time >= BASE.safeUntil) { this.openSaid = true; this.emit({ e: 'phase', name: 'bases-open' }); }
    if (!this.finalSaid && this.time >= BASE.decayFrom) { this.finalSaid = true; this.emit({ e: 'phase', name: 'final' }); }

    // spawning from every slot
    for (let p = 0; p < PLAYERS; p++) {
      const pl = this.players[p];
      if (pl.out) continue;
      const f = this.faction(p), { x: gx, y: gy } = this.gate(p);
      for (let k = 0; k < pl.slots.length; k++) {
        pl.slotT[k] -= DT;
        if (pl.slotT[k] > 0) continue;
        const cls = pl.slots[k];
        pl.slotT[k] += unitStats(cls, pl.tier, f).interval;
        if (pl.alive < ECON.cap) this.spawnUnit(p, cls, pl.tier, gx, gy);
      }
      // the hero: back from the dead when its wait is over, and mana (fast at the center)
      const h = pl.hero;
      if (h && h.i < 0 && this.time >= h.respawn) this.spawnHero(p);
      if (h && h.i >= 0) h.mana = Math.min(heroStats(h.id, h.level).mana, h.mana + this.manaRate(p) * DT);
      // the hard end: every base decays from minute 18
      if (this.time >= BASE.decayFrom) pl.baseHp -= BASE.decayPerSec * pl.baseMax * DT;
    }
    this.settleBases();
    // the center's waves at the leader, from minute 18
    if (this.time >= CENTER.waveFrom) {
      this.waveT -= DT;
      if (this.waveT <= 0) {
        this.waveT += CENTER.waveEvery;
        const tier = Math.max(...this.players.map((q) => q.tier));
        // Keep the spawn's one-cell jitter clear of the enlarged tower.
        const offset = TOWER_RADIUS + 1;
        for (let k = 0; k < CENTER.waveSize && this.neutralAlive < WAVE_CAP; k++) this.spawnUnit(NEUTRAL, k % 2 ? 'line' : 'spear', Math.min(4, tier), this.center.x + offset, this.center.y + offset);
      }
    }
    // the center's boss event: the giant and the camps, from 5:00 every 4:00
    if (this.bossWave < BOSS.waves && this.time >= bossAt(this.bossWave + 1)) this.wakeBoss();

    this.rehash();
    const leader = this.leader();
    const crowds = new Map<string, { count: number; radius: number }>();
    const pointKey = (t: number, g: { x: number; y: number }) => `${t}:${g.x}:${g.y}`;
    for (let i = 0; i < CAP; i++) {
      if (this.state[i] === STATE.dead || this.team[i] >= PLAYERS) continue;
      const g = this.goalOf(i);
      if (g.kind !== 'point') continue;
      const key = pointKey(this.team[i], g), crowd = crowds.get(key) ?? { count: 0, radius: 0 };
      crowd.count++; crowd.radius = Math.max(crowd.radius, this.rad[i]); crowds.set(key, crowd);
    }
    // each barracks' tower shoots the nearest enemy in range
    for (let p = 0; p < PLAYERS; p++) {
      const pl = this.players[p];
      if (pl.out || (this.towerT[p] -= DT) > 0) continue;
      const b = this.bases[p];
      let best = -1, bd = Infinity;
      this.each(b.x, b.y, TOWER.range, (j, d2) => { if (this.team[j] !== p && this.duel[j] < 0 && d2 < bd) { bd = d2; best = j; } });
      if (best < 0) continue;
      this.towerT[p] = TOWER.cd;
      this.emit({ e: 'shot', from: -1 - p, x0: b.x, y0: b.y, x1: this.x[best], y1: this.y[best], cls: 3, team: p });
      this.hurt(best, (TOWER.dmg + TOWER.dmgPerTier * pl.tier) * armorTaken(CLASSES[this.cls[best]], 'pierce'), -1);
    }
    for (let i = 0; i < CAP; i++) {
      if (this.state[i] === STATE.dead) continue;
      const cl = CLASSES[this.cls[i]], t = this.team[i];
      const owner = this.heroOf[i];
      // heroes stand outside their army's mechanic; a drummer's aura still heals them, as a class skill on any ally
      const f = t < PLAYERS && owner < 0 ? this.faction(t) : null, c = classOf(cl, f);
      this.calm[i] += DT;
      if (this.moving[i] && this.time >= this.moveUntil[i]) this.moving[i] = 0;
      this.think[i] -= DT;
      let tg = this.target[i];
      if (tg >= 0 && (this.state[tg] === STATE.dead || this.team[tg] === t || (this.duel[tg] >= 0 && this.duel[tg] !== i))) tg = -1;
      if (this.duel[i] >= 0) tg = this.duel[i];
      let bt = this.baseTarget[i];
      if (bt >= 0 && this.players[bt].out) bt = -1;
      const post = this.post[i];
      if (this.think[i] <= 0) {
        this.think[i] += THINK;
        const activeOrder = this.ord[i] >= 0 ? this.orders[this.ord[i]] : null;
        if (this.moving[i] && !activeOrder?.move
          && this.nearestFoe(i, Math.max(AGGRO, this.range[i] + 1), 0) < 0
          && this.nearBase(i, Math.max(this.range[i], 1.2) + 0.6) < 0) this.moving[i] = 0;
        // a move order walks past fights (a duel still holds it in the ring); a ram fights only what it touches
        tg = this.moving[i] && this.duel[i] < 0 ? -1 : this.nearestFoe(i, c.ram ? this.range[i] + 1 : Math.max(AGGRO, this.range[i] + 1), c.minRange ?? 0);
        // siege goes for a base in reach before any soldier (ROSTER.md: its job is breaking bases). Without it a push
        // spent 90 to 100% of its time on the defender's fresh spawns and every bot match ran to the hard end (2026-10-26)
        bt = !this.moving[i] && (tg < 0 || cl === 'siege' || cl === 'ram') ? this.nearBase(i, Math.max(this.range[i], 1.2) + 0.6) : -1;
        if (bt >= 0) tg = -1;
        if (post >= 0) bt = -1; // a camp or the giant guards its spot and never marches on a base
        // neighbours (Phalanx), the support's aura and Volley, refreshed at the think rate
        let n = 0, ranged = 0;
        this.each(this.x[i], this.y[i], 1.4, (j) => { if (j !== i && this.team[j] === t && this.heroOf[j] < 0) n++; }); // a hero is no shield wall (Astra F4)
        this.near[i] = n;
        if (cl === 'ranged' && f?.volley) this.each(this.x[i], this.y[i], 3, (j) => { if (j !== i && this.team[j] === t && this.cls[j] === 3) ranged++; });
        this.buff[i] = f?.volley && ranged >= f.volley.near ? 2 : 0;
        if (c.aura) {
          const heal = AURA.regen * (f?.physicians?.aura ?? 1) * THINK;
          this.each(this.x[i], this.y[i], AURA.radius, (j) => {
            if (this.team[j] !== t) return;
            this.hp[j] = Math.min(this.maxHp[j], this.hp[j] + this.maxHp[j] * heal);
            this.hasteT[j] = this.time + THINK + DT;
          });
        }
        if (owner >= 0) this.passive(i, owner);
      }
      // a guard lets go of a foe that leads it past its leash, and walks back (below)
      if (post >= 0 && tg >= 0 && hypot(this.x[tg] - this.home(post).x, this.y[tg] - this.home(post).y) - this.rad[tg] > (post === PLAYERS ? BOSS.range + 1 : BOSS.leash)) tg = -1;
      if (t < PLAYERS && this.players[t].drums > this.time) this.hp[i] = Math.min(this.maxHp[i], this.hp[i] + this.maxHp[i] * HR.drumsRegen * DT);
      const doc = f?.physicians;
      if (doc && this.calm[i] > doc.after) this.hp[i] = Math.min(this.maxHp[i], this.hp[i] + this.maxHp[i] * doc.regen * DT);
      if (owner >= 0 && this.calm[i] > HR.calmAfter) this.hp[i] = Math.min(this.maxHp[i], this.hp[i] + this.maxHp[i] * HR.calmRegen * DT);
      if (this.salveT[i] > 0) {
        const dt = Math.min(DT, this.salveT[i]);
        this.hp[i] = Math.min(this.maxHp[i], this.hp[i] + this.salveRate[i] * dt);
        this.salveT[i] = this.hp[i] >= this.maxHp[i] ? 0 : Math.max(0, this.salveT[i] - dt);
      }
      this.target[i] = tg; this.baseTarget[i] = bt;
      if (this.stunT[i] > this.time) { this.state[i] = STATE.idle; continue; } // stunned: no step, no strike
      this.cd[i] = Math.max(0, this.cd[i] - DT);

      let mx = 0, my = 0;
      /** One step along the owner's goal field (the center's waves go for the leader); false once there. */
      const march = () => {
        const k = (this.y[i] | 0) * SIZE + (this.x[i] | 0);
        // a ram goes only where a base is: its goal's, or home to its own gate when sent anywhere else (a move order is
        // the player's own, and goes where it says, after it arrives too: Astra 2026-10-26 F1)
        const o = this.ord[i], home = c.ram && !this.moving[i] && t < PLAYERS && !(o >= 0 && this.orders[o]!.move) && this.goalOf(i).kind !== 'base';
        const fld = home ? this.baseFields[t] : o >= 0 ? this.orders[o]!.field : t < PLAYERS ? this.players[t].field : this.baseFields[leader >= 0 ? leader : 0];
        const order = o >= 0 ? this.orders[o]! : null;
        const goal = order?.goal ?? (t < PLAYERS ? this.players[t].goal : null);
        const point = !home && goal?.kind === 'point' ? goal : null;
        const distance = point ? hypot(point.x - this.x[i], point.y - this.y[i]) : fld.dist[k];
        if (point) {
          const crowd = crowds.get(pointKey(t, point))!;
          const envelope = arrivalRadius(crowd.count, crowd.radius);
          // Reach the original arrival distance before retaining a crowd envelope.
          // Merely starting inside a large crowd disk must not cancel a short walk.
          if (distance <= this.moveArrive[i] && distance <= envelope)
            this.moveArrive[i] = Math.max(this.moveArrive[i], envelope);
          if (distance > this.moveArrive[i]) this.each(this.x[i], this.y[i], 2 * crowd.radius + 0.25, j => {
            if (j === i || this.team[j] !== t || this.state[j] !== STATE.idle || !this.moveSettled[j]) return;
            const other = this.goalOf(j);
            if (other.kind !== 'point' || other.x !== point.x || other.y !== point.y
              || hypot(point.x - this.x[j], point.y - this.y[j]) > envelope) return;
            const spacing = 2 * Math.max(this.rad[i], this.rad[j]) + 0.25;
            if (hypot(this.x[i] - this.x[j], this.y[i] - this.y[j]) <= spacing)
              this.moveArrive[i] = Math.max(this.moveArrive[i], envelope + spacing);
          });
        }
        const hold = point || this.moving[i] ? this.moveArrive[i] : home ? 2.5 : 0.5;
        // never settle inside the Giant's reach on the way to a point outside it (keep walking the field instead): a crowd
        // that stopped at its edge was hit by it (2026-10-29, Flash; the earlier straight push away from it oscillated)
        const boss = this.boss, reach = boss >= 0 ? this.range[boss] + this.rad[boss] + this.rad[i] : 0;
        const inReach = point && boss >= 0 && hypot(point.x - this.x[boss], point.y - this.y[boss]) > reach
          && hypot(this.x[i] - this.x[boss], this.y[i] - this.y[boss]) <= reach;
        if (distance <= hold && !inReach) { this.moving[i] = 0; this.moveSettled[i] = point ? 1 : 0; return false; }
        if (point && fld.dist[k] <= 1.5) {
          mx = (point.x - this.x[i]) / distance; my = (point.y - this.y[i]) / distance; return true;
        }
        mx = fld.dx[k]; my = fld.dy[k];
        return true;
      };
      if (tg >= 0) {
        const ddx = this.x[tg] - this.x[i], ddy = this.y[tg] - this.y[i], d = hypot(ddx, ddy) || 1e-6;
        this.face[i] = atan2(ddy, ddx);
        if (d <= this.range[i] + this.rad[tg]) {
          this.state[i] = STATE.attack;
          if (this.cd[i] === 0) this.attack(i, tg, cl, f);
          // Mounted Bow: Tomoe shoots without stopping her horse, but keeps her distance (riding on into the enemy got
          // her killed 9 to 26 times a match, harness 2026-10-26); a duel's ring holds her anyway
          if (owner >= 0 && this.players[owner].hero!.id === 'tomoe' && this.duel[i] < 0 && d > this.range[i] * 0.6) march();
        } else { mx = ddx / d; my = ddy / d; this.state[i] = STATE.walk; }
      } else if (bt >= 0) {
        const b = this.bases[bt];
        this.face[i] = atan2(b.y - this.y[i], b.x - this.x[i]);
        this.state[i] = STATE.attack;
        if (this.cd[i] === 0) {
          this.cd[i] = this.rateOf(i);
          if (this.shoots(i, cl)) this.emit({ e: 'shot', from: i, x0: this.x[i], y0: this.y[i], x1: b.x, y1: b.y, cls: owner >= 0 ? C.ranged : this.cls[i], team: t });
          if (this.time >= BASE.safeUntil) {
            this.players[bt].baseHp -= this.dmg[i] * (c.building ?? 1) * this.power(t);
            this.emit({ e: 'baseHit', p: bt });
          }
        }
      } else if (post >= 0) { // a guard with no foe walks back to its spot
        const hm = this.home(post), hx = hm.x - this.x[i], hy = hm.y - this.y[i], d = hypot(hx, hy);
        if (d > 2) { mx = hx / d; my = hy / d; this.state[i] = STATE.walk; } else this.state[i] = STATE.idle;
      } else this.state[i] = march() ? STATE.walk : STATE.idle;

      // separation from neighbours (pushed by how far into the space between bodies, along the line between centers)
      let px = 0, py = 0;
      const R = this.rad[i] * 2;
      this.each(this.x[i], this.y[i], R, (j, d2) => {
        const ox = this.x[i] - this.x[j], oy = this.y[i] - this.y[j], l = hypot(ox, oy);
        if (j === i || l < 1e-4 || (c.ram && this.team[j] === t)) return; // a ram's own soldiers make way for it
        const push = (R - Math.sqrt(d2)) / R;
        px += (ox / l) * push; py += (oy / l) * push;
      });
      const sp = this.speed[i] * (this.hasteT[i] > this.time ? AURA.speed : 1) * (this.slowT[i] > this.time ? HR.passSlow : 1) * DT;
      let vx = mx * sp + px * sp * 0.8, vy = my * sp + py * sp * 0.8;
      const vl = hypot(vx, vy);
      if (vl > sp * 1.5) { vx *= (sp * 1.5) / vl; vy *= (sp * 1.5) / vl; }
      if (vl > 1e-4 && this.state[i] === STATE.walk) { this.face[i] = atan2(vy, vx); if (cl === 'cavalry' && vl > sp * 0.6) this.charge[i] = 1; }
      const nx = this.x[i] + vx, ny = this.y[i] + vy, x0 = this.x[i], y0 = this.y[i];
      if (!this.blocked[(this.y[i] | 0) * SIZE + (nx | 0)]) this.x[i] = nx;
      if (!this.blocked[(ny | 0) * SIZE + (this.x[i] | 0)]) this.y[i] = ny;
      // The cell mask guides paths; the circular boundary keeps every body off the drawn plinth.
      if (i !== this.boss) {
        const ox = this.x[i] - this.center.x, oy = this.y[i] - this.center.y, d = hypot(ox, oy);
        const r = Math.max(TOWER_RADIUS, PLINTH_RADIUS + this.rad[i]);
        if (d > 0 && d < r) { this.x[i] = this.center.x + ox / d * r; this.y[i] = this.center.y + oy / d * r; }
      }
      if (this.duel[i] >= 0) { // no one leaves the ring
        const r = this.duels.find((d) => d.a === i || d.b === i)!, ox = this.x[i] - r.x, oy = this.y[i] - r.y, d = hypot(ox, oy);
        if (d > HR.duelRing) { this.x[i] = r.x + (ox / d) * HR.duelRing; this.y[i] = r.y + (oy / d) * HR.duelRing; }
      }
      // Once arrived, retain displacement caused by separation, not another ally's
      // expanded envelope. Such an ally can anchor others only inside the crowd disk.
      if (t < PLAYERS && this.moveSettled[i] && this.state[i] === STATE.idle) {
        const g = this.goalOf(i);
        if (g.kind === 'point') this.moveArrive[i] = Math.max(this.moveArrive[i], hypot(g.x - this.x[i], g.y - this.y[i]) + 0.05);
      }
      // the stride's phase by the ground actually covered, after the wall and the duel ring, so a soldier held where he
      // stands does not walk in place (a horse's hooves keep the ground by it, mount.ts gaitRow; 2026-10-27, Astra)
      if (this.state[i] === STATE.walk) this.phase[i] += hypot(this.x[i] - x0, this.y[i] - y0) * 4;
    }
    // duels run out; skill zones pulse on what is in them while their caster lives
    for (const d of [...this.duels]) if (this.time >= d.until) this.endDuel(d.a);
    for (const z of [...this.zones]) {
      // a zone whose caster fell this tick (maybe to an earlier zone) is gone from the list: skip it (Astra F3)
      if (!this.zones.includes(z) || this.players[z.p].hero?.i !== z.src) continue;
      if (z.follow) { z.x = this.x[z.src]; z.y = this.y[z.src]; }
      // a zone of `time` seconds pulses time / every times, one for each `every` it covers (it pulsed once more at its last
      // instant, so Relief of Orleans healed 42.5% for the 40% its card prints; tests/numbers.test.ts)
      if (this.time >= z.next && z.next < z.until - 1e-6) { z.next += z.every; this.pulse(z); }
      const k = this.zones.indexOf(z);
      if (this.time >= z.until && k >= 0) this.zones.splice(k, 1);
    }

    this.settleBases();
    // last base standing
    const left = this.players.map((pl, p) => (pl.out ? -1 : p)).filter((p) => p >= 0);
    if (left.length === 1) { this.winner = left[0]; this.emit({ e: 'win', p: this.winner }); }
    else if (left.length === 0) { this.winner = leader >= 0 ? leader : 0; this.emit({ e: 'win', p: this.winner }); }
    this.stepping = false;
  }

  private attack(i: number, tg: number, cl: ClassId, f: FactionDef | null) {
    const c = classOf(cl, f), owner = this.heroOf[i], h = owner >= 0 ? this.players[owner].hero! : null;
    this.cd[i] = this.rateOf(i);
    if (h) {
      // Two Swords: each strike on the same foe comes faster
      h.stack = this.lastHit[i] === tg ? Math.min(HR.twoSwordsMax, h.stack + 1) : 0;
      if (h.id === 'musashi') this.cd[i] *= 1 - HR.twoSwordsStep * h.stack;
    }
    let amount = this.dmg[i];
    if (f?.firstStrike && this.lastHit[i] !== tg) amount *= f.firstStrike;
    if (cl === 'cavalry' && this.charge[i]) { amount *= 2; this.charge[i] = 0; }
    this.lastHit[i] = tg;
    const t = this.team[i];
    // a bolt flies on through its target to its range (Astra 2026-10-26 F2)
    const fly = c.bolt ? this.range[i] / (hypot(this.x[tg] - this.x[i], this.y[tg] - this.y[i]) || 1e-6) : 1;
    if (this.shoots(i, cl)) this.emit({ e: 'shot', from: i, x0: this.x[i], y0: this.y[i], x1: this.x[i] + (this.x[tg] - this.x[i]) * fly, y1: this.y[i] + (this.y[tg] - this.y[i]) * fly, cls: owner >= 0 ? C.ranged : this.cls[i], team: t });
    else this.emit({ e: 'hit', x: this.x[tg], y: this.y[tg], cls: this.cls[i], team: t });
    const splash = i === this.boss ? BOSS.splash : c.splash;
    if (i === this.boss) this.emit({ e: 'slam', x: this.x[tg], y: this.y[tg], r: splash! });
    if (splash) {
      const hx = this.x[tg], hy = this.y[tg];
      const hits = this.foes(hx, hy, splash, t);
      for (const j of hits) this.damage(i, j, j === tg ? amount : amount * 0.5);
    } else if (c.bolt) { // the Great Bow's bolt: its target, then half to every foe on the line out to its range
      const x0 = this.x[i], y0 = this.y[i], R = this.range[i], d = hypot(this.x[tg] - x0, this.y[tg] - y0) || 1e-6;
      const ux = (this.x[tg] - x0) / d, uy = (this.y[tg] - y0) / d;
      this.damage(i, tg, amount);
      for (const j of this.foes(x0 + (ux * R) / 2, y0 + (uy * R) / 2, R / 2 + c.bolt, t)) {
        const ox = this.x[j] - x0, oy = this.y[j] - y0, along = ox * ux + oy * uy;
        if (j !== tg && along > 0 && along <= R && Math.abs(ox * uy - oy * ux) <= c.bolt + this.rad[j]) this.damage(i, j, amount * 0.5);
      }
    } else this.damage(i, tg, amount);
    // Shield Bash: Lagertha's every fourth strike stuns
    if (h?.id === 'lagertha' && ++h.bash % HR.bashEvery === 0 && this.state[tg] !== STATE.dead && this.canHurt(i, tg)) this.stun(tg, HR.bashStun);
  }

  /** A hero's passive, at its think rate: Joan's banner heals and hurries, Liang's drum quickens, Leonidas's shields. */
  private passive(i: number, owner: number) {
    const id = this.players[owner].hero!.id, ps = HERO[id].passive, t = this.team[i];
    if (id === 'joan') for (const j of this.allies(this.x[i], this.y[i], ps.r!, t)) { this.hp[j] = Math.min(this.maxHp[j], this.hp[j] + this.maxHp[j] * ps.m! * THINK); this.hasteT[j] = this.time + THINK + DT; }
    else if (id === 'liang') for (const j of this.allies(this.x[i], this.y[i], ps.r!, t)) this.quickT[j] = this.time + THINK + DT;
    else if (id === 'leonidas') for (const j of this.allies(this.x[i], this.y[i], ps.r!, t)) this.ward(j, ps.m!, this.time + THINK + DT);
  }
  /** One pulse of a skill zone on what stands in it. */
  private pulse(z: Zone) {
    const src = z.src, t = this.team[src], live = () => this.players[z.p].hero?.i === src;
    if (z.kind === 'relief') { for (const j of this.allies(z.x, z.y, z.r, t)) this.hp[j] = Math.min(this.maxHp[j], this.hp[j] + this.maxHp[j] * z.m * z.every); return; }
    if (z.kind === 'pass') for (const j of this.allies(z.x, z.y, z.r, t)) this.ward(j, z.m, this.time + z.every + DT);
    const hits = this.foes(z.x, z.y, z.r, t).filter((j) => this.canHurt(src, j));
    if (z.kind === 'storm') hits.slice(0, 6).forEach((j) => this.emit({ e: 'shot', from: src, x0: this.x[j] - 2, y0: this.y[j] - 3, x1: this.x[j], y1: this.y[j], cls: C.ranged, team: t }));
    for (const j of hits) {
      if (this.state[j] === STATE.dead || !live()) continue;
      if (z.kind === 'pass') this.slowT[j] = this.time + z.every + DT;
      else if (z.kind === 'stirling') this.stun(j, z.m);
      else {
        this.damage(src, j, this.dmg[src] * z.m);
        if (z.kind === 'spin' && this.state[j] !== STATE.dead) this.shove(j, z.x - this.x[j], z.y - this.y[j], Math.min(HR.spinPull, hypot(z.x - this.x[j], z.y - this.y[j]) - 1));
      }
    }
  }

  /** Where a guard stands: its team's camp on the road in, or the giant's tower. */
  home(post: number) { return post === PLAYERS ? this.center : this.campSpot(post); }
  /** Team p's camp: on its road in, BOSS.campAt from the center, just outside the giant's reach. */
  campSpot(p: number) {
    const b = this.bases[p], c = this.center, d = hypot(b.x - c.x, b.y - c.y);
    return { x: c.x + ((b.x - c.x) / d) * BOSS.campAt, y: c.y + ((b.y - c.y) / d) * BOSS.campAt };
  }
  /** When the next wave of the boss event wakes, or Infinity after the last. */
  nextBossAt() { return this.bossWave < BOSS.waves ? bossAt(this.bossWave + 1) : Infinity; }
  /** A wave: the giant wakes on the tower (or, still standing, grows to this wave and heals), and every team still in
   *  gets its camp topped back up. Everything grows BOSS.growth a wave and matches the top tier in the match: camps take
   *  that tier's soldiers, the giant's numbers scale with that tier's budget (BOSS's are a tier 1 giant's). */
  private wakeBoss() {
    const w = ++this.bossWave, grow = 1 + BOSS.growth * (w - 1), tier = Math.max(1, ...this.players.map((q) => q.tier));
    const grew = this.boss >= 0, i = grew ? this.boss : this.spawnUnit(NEUTRAL, 'elite', tier, this.center.x, this.center.y);
    if (i >= 0) {
      this.x[i] = this.center.x; this.y[i] = this.center.y; this.post[i] = PLAYERS; this.rad[i] = BOSS.radius;
      this.hp[i] = this.maxHp[i] = BOSS.hp * grow * (BUDGET_HP[tier] / BUDGET_HP[1]); this.dmg[i] = BOSS.dmg * grow * (BUDGET_DPS[tier] / BUDGET_DPS[1]); this.rate[i] = BOSS.cd; this.range[i] = BOSS.range; this.speed[i] = 0;
      this.boss = i;
      this.emit({ e: 'boss', wave: w, i, grew });
    }
    for (let k = 0; k < PLAYERS; k++) {
      if (this.players[k].out) continue;
      let have = 0;
      for (let j = 0; j < CAP; j++) if (this.state[j] !== STATE.dead && this.post[j] === k) have++;
      const at = this.campSpot(k);
      for (let n = have; n < BOSS.camp.length; n++) {
        const j = this.spawnUnit(NEUTRAL, BOSS.camp[n], tier, at.x, at.y);
        if (j < 0) break;
        this.post[j] = k; this.hp[j] = this.maxHp[j] *= grow; this.dmg[j] *= grow;
      }
    }
  }

  /** Knock out every base at 0 HP, but never the last ones at once: if every base still in would fall on the same
   *  tick (the hard end's decay, or a trade), the one with the most HP left before this tick survives. */
  private settleBases() {
    const inPlay = this.players.map((pl, p) => p).filter((p) => !this.players[p].out);
    const falling = inPlay.filter((p) => this.players[p].baseHp <= 0);
    if (!falling.length) return;
    if (falling.length === inPlay.length) {
      const keep = falling.reduce((a, b) => (this.players[b].baseHp > this.players[a].baseHp ? b : a));
      this.players[keep].baseHp = 1;
      falling.splice(falling.indexOf(keep), 1);
    }
    for (const p of falling) this.eliminate(p);
  }

  private eliminate(p: number) {
    const pl = this.players[p];
    if (pl.out) return;
    pl.out = true; pl.baseHp = 0;
    for (let i = 0; i < CAP; i++) if (this.state[i] !== STATE.dead && this.team[i] === p) this.kill(i, -1);
    this.emit({ e: 'out', p });
  }

  /** The player the center's waves hunt: most kills among those still in. */
  leader() {
    let best = -1, bk = -1;
    this.players.forEach((pl, p) => { if (!pl.out && pl.kills > bk) { bk = pl.kills; best = p; } });
    return best;
  }

  live() { let n = this.neutralAlive; for (const p of this.players) n += p.alive; return n; }

  /** A cheap state hash for determinism tests. */
  hash() {
    let h = 2166136261;
    const mix = (v: number) => { h ^= Math.round(v * 64) | 0; h = Math.imul(h, 16777619); };
    for (let i = 0; i < CAP; i++) if (this.state[i]) { mix(this.x[i]); mix(this.y[i]); mix(this.hp[i]); mix(this.cls[i]); mix(this.ord[i]); mix(this.squad[i]); }
    for (const p of this.players) { mix(p.gold); mix(p.baseHp); mix(p.tier); mix(p.hero?.level ?? 0); mix(p.hero?.xp ?? 0); }
    return (h >>> 0).toString(16);
  }
}
